import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Slider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Radio,
  ListItemSecondaryAction,
} from "@mui/material";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import AddCardOutlinedIcon from "@mui/icons-material/AddCardOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import CurrencyBitcoinOutlinedIcon from "@mui/icons-material/CurrencyBitcoinOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useNavigate, useSearchParams } from "react-router-dom";
import AdminPageHeader from "../../../components/common/AdminPageHeader";
import LoadingState from "../../../components/common/LoadingState";
import EmptyState from "../../../components/common/EmptyState";
import { WalletCards } from "lucide-react";

import {
  applyCouponCode,
  createPayment,
  createWalletCharge,
  getPendingPayments,
  getWalletBalance,
} from "../../../services/walletService";

// ایمپورت سرویس لوکاپ داده‌ها
import { getLookupData } from "../../../services/lookupService";

const INVOICE_PAYMENT_STRATEGIES = [
  { value: "wallet", label: "Wallet only" },
  { value: "online", label: "Online only" },
  { value: "split", label: "Wallet + Online" },
];

// نگاشت آیکون‌ها بر اساس عنوان درگاه‌های دریافتی از بک‌اند
const GATEWAY_ICON_MAP = {
  visa: <CreditCardOutlinedIcon fontSize="small" />,
  mastercard: <CreditCardOutlinedIcon fontSize="small" />,
  cryptocurrency: <CurrencyBitcoinOutlinedIcon fontSize="small" />,
  crypto: <CurrencyBitcoinOutlinedIcon fontSize="small" />,
  paypal: <PaymentsOutlinedIcon fontSize="small" />,
};

function getGatewayIcon(title) {
  const key = String(title || "").toLowerCase();
  for (const iconKey of Object.keys(GATEWAY_ICON_MAP)) {
    if (key.includes(iconKey)) {
      return GATEWAY_ICON_MAP[iconKey];
    }
  }
  return <PaymentsOutlinedIcon fontSize="small" />;
}

function unwrapEnvelope(response) {
  if (response == null) return null;
  if (Array.isArray(response)) return response;
  if (
    response.BALANCE != null ||
    response.balance != null ||
    response.WALLET_ID != null
  ) {
    return response;
  }
  if (Array.isArray(response.items)) return response.items;
  if (response.data != null) {
    if (Array.isArray(response.data)) return response.data;
    if (response.data?.data != null) return response.data.data;
    return response.data;
  }
  return response;
}

function getFirstActiveWallet(walletData) {
  const rows = Array.isArray(walletData)
    ? walletData
    : walletData
      ? [walletData]
      : [];
  return (
    rows.find((row) => String(row?.IS_ACTIVE).toLowerCase() === "true") ||
    rows[0] ||
    null
  );
}

function formatMoney(amount, code = "") {
  const formatted = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
  }).format(Number(amount || 0));
  return code ? `${formatted} ${code}` : formatted;
}

function formatDate(dateValue) {
  if (!dateValue) return "";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return String(dateValue);
  return new Intl.DateTimeFormat("en-GB", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  }).format(date);
}

function clampNumber(value, min, max) {
  const n = Number(value);
  if (Number.isNaN(n)) return min;
  return Math.min(max, Math.max(min, n));
}

function getRedirectUrl(response) {
  return (
    response?.redirectUrl ??
    response?.REDIRECT_URL ??
    response?.paymentUrl ??
    response?.PAYMENT_URL ??
    response?.url
  );
}

function Wallet() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paymentRef = searchParams.get("payment");

  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const [error, setError] = useState("");
  const [loadError, setLoadError] = useState("");
  const [payError, setPayError] = useState("");
  const [success, setSuccess] = useState("");
  const [successContext, setSuccessContext] = useState("");

  const [wallet, setWallet] = useState({
    id: null,
    balance: 0,
    currency: "USD",
    currencyName: "",
    symbol: "",
    lastTransactionDate: null,
  });

  const [pendingPayments, setPendingPayments] = useState([]);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);

  const [couponCode, setCouponCode] = useState("");
  const [discount, setDiscount] = useState(null);

  const [topUpAmount, setTopUpAmount] = useState("");
  const [topUpMethod, setTopUpMethod] = useState("");

  const [strategy, setStrategy] = useState("online");
  const [onlineMethod, setOnlineMethod] = useState("");
  const [walletPayAmount, setWalletPayAmount] = useState(0);

  // استیت نگهداری درگاه‌های پرداخت دریافتی از سرویس لوکاپ
  const [onlineGateways, setOnlineGateways] = useState([]);

  const refreshWalletBalance = useCallback(async () => {
    const walletResponse = await getWalletBalance();
    const walletData = unwrapEnvelope(walletResponse);
    const activeWallet = getFirstActiveWallet(walletData);
    setWallet({
      id: activeWallet?.WALLET_ID ?? activeWallet?.walletId ?? null,
      balance: Number(activeWallet?.BALANCE ?? activeWallet?.balance ?? 0),
      currency: activeWallet?.CODE ?? activeWallet?.currency ?? "USD",
      currencyName:
        activeWallet?.CURRENCY_NAME ?? activeWallet?.currencyName ?? "",
      symbol: activeWallet?.SYMBOL?.trim() ?? "",
      lastTransactionDate:
        activeWallet?.LAST_TRANSACTION_DATE ??
        activeWallet?.lastTransactionDate ??
        null,
    });
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      try {
        setLoading(true);
        setError("");
        setLoadError("");

        // فراخوانی همزمان اطلاعات کیف‌پول، پرداخت‌های معلق و درگاه‌های پرداخت (Lookup)
        const [walletResponse, pendingResponse, gatewaysResponse] =
          await Promise.all([
            getWalletBalance(),
            getPendingPayments({ paymentRef }),
            getLookupData("PAYMENT_GATEWAY"),
          ]);

        const walletData = unwrapEnvelope(walletResponse);
        const pendingData = unwrapEnvelope(pendingResponse);
        const activeWallet = getFirstActiveWallet(walletData);

        const normalizedWallet = {
          id: activeWallet?.WALLET_ID ?? activeWallet?.walletId ?? null,
          balance: Number(activeWallet?.BALANCE ?? activeWallet?.balance ?? 0),
          currency: activeWallet?.CODE ?? activeWallet?.currency ?? "USD",
          currencyName:
            activeWallet?.CURRENCY_NAME ?? activeWallet?.currencyName ?? "",
          symbol: activeWallet?.SYMBOL?.trim() ?? "",
          lastTransactionDate:
            activeWallet?.LAST_TRANSACTION_DATE ??
            activeWallet?.lastTransactionDate ??
            null,
        };

        const rawPendingItems = Array.isArray(pendingData)
          ? pendingData
          : Array.isArray(pendingData?.items)
            ? pendingData.items
            : [];

        const normalizedPendingPayments = rawPendingItems
          .filter((item) => String(item?.ERROR_NUMBER ?? "0") === "0")
          .map((item) => ({
            id: item.INVOICE_ID ?? item.id,
            invoiceNumber:
              item.INVOICE_NUMBER ??
              item.invoiceNumber ??
              `Invoice #${item.INVOICE_ID ?? item.id}`,
            amount: Number(item.TOTAL_AMOUNT ?? item.amount ?? 0),
            currency: item.CODE ?? item.currency ?? "AFN",
            symbol: item.SYMBOL?.trim() ?? item.symbol ?? "",
            invoiceDate: item.INVOICE_DATE ?? item.invoiceDate ?? null,
            dueDate: item.DUE_DATE ?? item.dueDate ?? null,
            status: "Pending",
          }));

        // نرمال‌سازی درگاه‌های دریافتی از خروجی دقیق لوکاپ بک‌اند
        const rawGateways = unwrapEnvelope(gatewaysResponse) || [];
        const normalizedGateways = rawGateways.map((g) => ({
          value:
            g.ENUM_PAYMENT_GATEWAY_ID ??
            g.enumPaymentGatewayId ??
            g.value ??
            g.id ??
            "",
          label: g.GATEWAY_TITLE ?? g.gatewayTitle ?? g.label ?? g.text ?? "",
        }));

        if (!mounted) return;

        setWallet(normalizedWallet);
        setPendingPayments(normalizedPendingPayments);
        setOnlineGateways(normalizedGateways);

        // تنظیم درگاه پیش‌فرض در صورت وجود درگاه فعال
        if (normalizedGateways.length > 0) {
          setTopUpMethod(normalizedGateways[0].value);
          setOnlineMethod(normalizedGateways[0].value);
        }

        if (normalizedPendingPayments.length > 0) {
          const target = paymentRef
            ? normalizedPendingPayments.find(
                (x) =>
                  String(x.id) === String(paymentRef) ||
                  String(x.invoiceNumber) === String(paymentRef),
              )
            : null;
          setSelectedInvoiceId(
            target ? target.id : normalizedPendingPayments[0].id,
          );
        }
      } catch (e) {
        if (!mounted) return;
        setLoadError(e?.message || "Failed to load wallet data.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadData();
    return () => {
      mounted = false;
    };
  }, [paymentRef]);

  const currentInvoice = useMemo(() => {
    if (!selectedInvoiceId || pendingPayments.length === 0) return null;
    return (
      pendingPayments.find((item) => item.id === selectedInvoiceId) ||
      pendingPayments[0]
    );
  }, [selectedInvoiceId, pendingPayments]);

  const invoiceCurrency = useMemo(
    () => currentInvoice?.currency || "",
    [currentInvoice],
  );

  const subtotal = useMemo(
    () => (currentInvoice ? Number(currentInvoice.amount) : 0),
    [currentInvoice],
  );

  const discountAmount = useMemo(
    () =>
      Number(
        discount?.amount ??
          discount?.DISCOUNT_AMOUNT ??
          discount?.discountAmount ??
          0,
      ),
    [discount],
  );

  const total = Math.max(0, subtotal - discountAmount);
  const hasPendingPayments = pendingPayments.length > 0;

  const hasSameCurrency =
    !invoiceCurrency ||
    !wallet.currency ||
    wallet.currency.toUpperCase() === invoiceCurrency.toUpperCase();

  const maxWalletForInvoice = useMemo(() => {
    if (!hasSameCurrency || !hasPendingPayments) return 0;
    return Math.min(wallet.balance, total);
  }, [hasSameCurrency, hasPendingPayments, wallet.balance, total]);

  const isWalletEnough =
    hasPendingPayments &&
    hasSameCurrency &&
    wallet.balance >= total &&
    total > 0;

  useEffect(() => {
    if (!hasPendingPayments) {
      setStrategy("online");
      setWalletPayAmount(0);
      return;
    }
    if (!hasSameCurrency) {
      setStrategy("online");
      setWalletPayAmount(0);
      return;
    }
    if (isWalletEnough) {
      setStrategy("wallet");
      setWalletPayAmount(total);
    } else if (wallet.balance > 0 && total > 0) {
      setStrategy("split");
      setWalletPayAmount(wallet.balance);
    } else {
      setStrategy("online");
      setWalletPayAmount(0);
    }
  }, [
    selectedInvoiceId,
    hasPendingPayments,
    hasSameCurrency,
    isWalletEnough,
    wallet.balance,
    total,
  ]);

  const walletPortion = useMemo(() => {
    if (strategy === "wallet") return total;
    if (strategy === "online") return 0;
    return clampNumber(walletPayAmount, 0, maxWalletForInvoice);
  }, [strategy, walletPayAmount, total, maxWalletForInvoice]);

  const onlinePortion = useMemo(
    () => Math.max(0, total - walletPortion),
    [total, walletPortion],
  );

  const handleApplyCoupon = async () => {
    const normalizedCoupon = couponCode.trim();
    if (!normalizedCoupon) return;

    try {
      setActionLoading(true);
      setError("");
      setPayError("");
      setSuccess("");
      setSuccessContext("");
      const result = await applyCouponCode({
        code: normalizedCoupon,
        paymentRef: currentInvoice?.id || paymentRef,
      });
      setDiscount(result);
      setSuccess(
        result?.message ?? result?.MESSAGE ?? "Coupon applied successfully.",
      );
      setSuccessContext("coupon");
    } catch (e) {
      setDiscount(null);
      setPayError(e?.message || "Coupon is invalid.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleTopUp = async () => {
    const amount = Number(topUpAmount);
    if (!amount || amount <= 0) {
      setError("Enter a valid amount to charge your wallet.");
      return;
    }

    try {
      setActionLoading(true);
      setError("");
      setPayError("");
      setSuccess("");
      setSuccessContext("");

      const response = await createWalletCharge({
        purpose: "topup",
        amount,
        currency: wallet.currency,
        method: topUpMethod, // ENUM_PAYMENT_GATEWAY_ID ارسال می‌شود
      });

      const redirectUrl = getRedirectUrl(response);
      if (redirectUrl) {
        window.open(redirectUrl, "_blank", "noopener,noreferrer");
        setSuccess("Redirecting to the payment gateway in a new tab...");
        setSuccessContext("topup");
        return;
      }

      await refreshWalletBalance();
      setTopUpAmount("");
      setSuccess(
        "Wallet top-up initiated. Balance will update after confirmation.",
      );
      setSuccessContext("topup");
    } catch (e) {
      setError(e?.message || "Wallet top-up failed.");
    } finally {
      setActionLoading(false);
    }
  };

  const handlePay = async () => {
    if (!hasPendingPayments || total <= 0) {
      setPayError("There are no pending payments to process.");
      return;
    }

    const invoiceId = currentInvoice?.id;

    if (!invoiceId) {
      setPayError("Please select an invoice from the list to pay.");
      return;
    }

    if ((strategy === "wallet" || strategy === "split") && !hasSameCurrency) {
      setPayError(
        `Wallet currency (${wallet.currency}) does not match the invoice currency (${invoiceCurrency}). Please use online payment instead.`,
      );
      return;
    }

    if (strategy === "wallet" && wallet.balance < total) {
      setPayError("Your wallet balance is insufficient for a full payment.");
      return;
    }

    if (strategy === "split" && walletPortion <= 0) {
      setPayError("Please enter a valid wallet portion for split payment.");
      return;
    }

    if (onlinePortion > 0 && !onlineMethod) {
      setPayError("Please select an online payment method.");
      return;
    }

    const normalizedWalletAmount =
      strategy === "wallet" || strategy === "split" ? walletPortion : 0;

    const normalizedGatewayAmount =
      strategy === "online" || strategy === "split" ? onlinePortion : 0;

    try {
      setActionLoading(true);
      setError("");
      setPayError("");
      setSuccess("");
      setSuccessContext("");

      const response = await createPayment({
        invoiceId,
        paymentMethod: strategy,
        enumPaymentGatewayId: normalizedGatewayAmount > 0 ? onlineMethod : null,
        transactionId: "",
        paymentResponse: {
          source: "wallet-page",
          couponCode: couponCode.trim() || null,
          walletId: wallet.id,
          invoiceCurrency,
          walletCurrency: wallet.currency,
        },
        walletAmount: normalizedWalletAmount,
        gatewayAmount: normalizedGatewayAmount,
      });

      const redirectUrl = getRedirectUrl(response);

      if (normalizedGatewayAmount > 0) {
        if (redirectUrl) {
          window.open(redirectUrl, "_blank", "noopener,noreferrer");
          setSuccess("Redirecting to the payment gateway in a new tab...");
          setSuccessContext("payment");
          return;
        }
        navigate(
          `/wallet/processing?payment=${encodeURIComponent(invoiceId)}`,
        );

        return;
      }

      await refreshWalletBalance();

      const remainingPayments = pendingPayments.filter(
        (item) => String(item.id) !== String(invoiceId),
      );
      setPendingPayments(remainingPayments);

      if (remainingPayments.length > 0) {
        setSelectedInvoiceId(remainingPayments[0].id);
      } else {
        setSelectedInvoiceId(null);
      }

      setDiscount(null);
      setCouponCode("");
      setWalletPayAmount(0);

      setSuccess(
        response?.message ||
          response?.MESSAGE ||
          "Payment completed successfully from your wallet.",
      );
      setSuccessContext("payment");
    } catch (e) {
      setPayError(
        e?.response?.data?.message ||
          e?.response?.data?.MESSAGE ||
          e?.message ||
          "Payment failed. Please try again.",
      );
      await refreshWalletBalance();
    } finally {
      setActionLoading(false);
    }
  };

  const payDisabled =
    actionLoading ||
    !hasPendingPayments ||
    total <= 0 ||
    (strategy === "wallet" && (!hasSameCurrency || wallet.balance < total)) ||
    (strategy === "split" &&
      hasSameCurrency &&
      walletPortion + onlinePortion < total - 0.001);

  if (loading) {
    return (
      <Box sx={{ minHeight: 420, bgcolor: "var(--background)" }}>
        <AdminPageHeader icon={WalletCards} title="Wallet" description="Top up balance, pay invoices, or combine wallet and online payment." />
        <LoadingState variant="card" label="Loading wallet" />
      </Box>
    );
  }

  if (loadError) {
    return <Box sx={{ bgcolor: "var(--background)" }}><AdminPageHeader icon={WalletCards} title="Wallet" description="Top up balance, pay invoices, or combine wallet and online payment." /><div className="admin-data-error" role="alert">{loadError}</div></Box>;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        px: 0,
        py: 0,
        bgcolor: "var(--background)",
      }}
    >
      <Box>
        <Stack spacing={2.5}>
          <AdminPageHeader
            icon={WalletCards}
            title="Wallet"
            description="Top up balance, pay invoices, or combine wallet and online payment."
            actions={<Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Chip
                  color="primary"
                  variant="outlined"
                  icon={<AccountBalanceWalletOutlinedIcon />}
                  label={`Balance: ${formatMoney(wallet.balance, wallet.currency)}`}
                />
                <Chip
                  variant="outlined"
                  icon={<ReceiptLongOutlinedIcon />}
                  label={`${pendingPayments.length} Pending`}
                />
                {invoiceCurrency ? (
                  <Chip
                    variant="outlined"
                    icon={<PaymentsOutlinedIcon />}
                    label={`Current Invoice Currency: ${invoiceCurrency}`}
                  />
                ) : null}
              </Stack>}
          />

          {error ? (
            <Alert severity="error" onClose={() => setError("")}>
              {error}
            </Alert>
          ) : null}
          {/* Top up wallet */}
          <Card variant="outlined" sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: { xs: 2.25, md: 3 } }}>
              <Stack spacing={2}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <AddCardOutlinedIcon color="primary" />
                  <Box>
                    <Typography variant="h6" fontWeight={800}>
                      Charge wallet
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Add funds via card, crypto, or PayPal. Balance updates
                      after payment confirmation.
                    </Typography>
                  </Box>
                </Stack>

                {success && successContext === "topup" && (
                  <Alert severity="success" onClose={() => setSuccess("")}>{success}</Alert>
                )}

                <Grid container spacing={2} alignItems="stretch">
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Amount"
                      value={topUpAmount}
                      disabled={actionLoading}
                      onChange={(e) => setTopUpAmount(e.target.value)}
                      sx={{ "& .MuiInputBase-root": { height: 40 } }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {wallet.currency}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <FormControl fullWidth size="small">
                      <Select
                        value={topUpMethod}
                        sx={{ height: 40 }}
                        onChange={(e) => setTopUpMethod(e.target.value)}
                        disabled={actionLoading || onlineGateways.length === 0}
                      >
                        {onlineGateways.map((item) => (
                          <MenuItem key={item.value} value={item.value}>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              {getGatewayIcon(item.label)}
                              <span>{item.label}</span>
                            </Stack>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      disabled={
                        actionLoading || !Number(topUpAmount) || !topUpMethod
                      }
                      onClick={handleTopUp}
                      sx={{
                        height: 40,
                        textTransform: "none",
                        fontWeight: 700,
                      }}
                    >
                      {actionLoading ? "Please wait..." : "Charge wallet"}
                    </Button>
                  </Grid>
                </Grid>
              </Stack>
            </CardContent>
          </Card>

          <Grid container spacing={2.5}>
            <Grid size={{ xs: 12, lg: 4 }}>
              <Card
                variant="outlined"
                sx={{
                  height: "100%",
                  borderRadius: 3,
                  background: "var(--card-bg)",
                }}
              >
                <CardContent sx={{ p: { xs: 2.25, md: 3 } }}>
                  <Stack spacing={2.25}>
                    <Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Wallet Balance
                      </Typography>
                      <Typography variant="h4" fontWeight={800}>
                        {formatMoney(wallet.balance, wallet.currency)}
                      </Typography>
                    </Box>
                    <Divider />
                    <Stack spacing={1.25}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={800}
                        color="text.primary"
                      >
                        Payment Summary{" "}
                        {currentInvoice
                          ? `(for ${currentInvoice.invoiceNumber})`
                          : ""}
                      </Typography>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Subtotal
                        </Typography>
                        <Typography variant="body2" fontWeight={700}>
                          {formatMoney(subtotal, invoiceCurrency)}
                        </Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Discount
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          color="success.main"
                        >
                          -{formatMoney(discountAmount, invoiceCurrency)}
                        </Typography>
                      </Stack>
                      <Divider />
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="subtitle1" fontWeight={800}>
                          Total Due
                        </Typography>
                        <Typography
                          variant="h6"
                          fontWeight={800}
                          color="primary.main"
                        >
                          {formatMoney(total, invoiceCurrency)}
                        </Typography>
                      </Stack>
                      {hasPendingPayments &&
                      hasSameCurrency &&
                      strategy === "split" ? (
                        <>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">
                              From wallet
                            </Typography>
                            <Typography variant="body2" fontWeight={700}>
                              {formatMoney(walletPortion, invoiceCurrency)}
                            </Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">
                              Online
                            </Typography>
                            <Typography variant="body2" fontWeight={700}>
                              {formatMoney(onlinePortion, invoiceCurrency)}
                            </Typography>
                          </Stack>
                        </>
                      ) : null}
                    </Stack>

                    {hasPendingPayments && !hasSameCurrency ? (
                      <Alert severity="warning" sx={{ fontSize: "0.825rem" }}>
                        Wallet is <b>{wallet.currency}</b>, invoices are{" "}
                        <b>{invoiceCurrency}</b>. Charge wallet in invoice
                        currency or pay online only.
                      </Alert>
                    ) : null}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, lg: 8 }}>
              <Card variant="outlined" sx={{ borderRadius: 3, height: "100%" }}>
                <CardContent sx={{ p: { xs: 2.25, md: 3 } }}>
                  <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
                    Pending payments
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2.5 }}
                  >
                    Select an invoice below to process your payment.
                  </Typography>

                  {!hasPendingPayments ? (
                    <EmptyState title="No pending payments" description="Paid invoices will appear in Billing." />
                  ) : (
                    <List disablePadding sx={{ display: "grid", gap: 1.25 }}>
                      {pendingPayments.map((item) => {
                        const isSelected = selectedInvoiceId === item.id;
                        return (
                          <Paper
                            key={item.id}
                            component="label"
                            variant="outlined"
                            sx={{
                              borderRadius: 2.5,
                              cursor: "pointer",
                              transition: "all 0.2s ease-in-out",
                              border: isSelected ? "2px solid" : "1px solid",
                              borderColor: isSelected
                                ? "primary.main"
                                : "divider",
                              bgcolor: isSelected
                                ? "var(--primary-muted)"
                                : "var(--card-bg)",
                              "&:hover": {
                                borderColor: "primary.light",
                                bgcolor: isSelected ? "var(--primary-muted)" : "var(--card-bg)",
                              },
                            }}
                          >
                            <ListItem>
                              <ListItemAvatar>
                                <Avatar
                                  sx={{
                                    bgcolor: isSelected
                                      ? "primary.main"
                                      : "var(--primary-muted)",
                                    color: isSelected ? "#fff" : "primary.main",
                                    transition: "all 0.2s",
                                  }}
                                >
                                  {isSelected ? (
                                    <CheckCircleIcon />
                                  ) : (
                                    <ReceiptLongOutlinedIcon />
                                  )}
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={item.invoiceNumber}
                                secondary={`Due: ${formatDate(item.dueDate)}`}
                                primaryTypographyProps={{
                                  component: "div",
                                  fontWeight: isSelected ? 700 : 500,
                                }}
                                secondaryTypographyProps={{ component: "div" }}
                              />
                              <ListItemSecondaryAction
                                sx={{ right: 24, position: "relative" }}
                              >
                                <Typography
                                  fontWeight={800}
                                  color={
                                    isSelected ? "primary.main" : "text.primary"
                                  }
                                >
                                  {formatMoney(item.amount, item.currency)}
                                </Typography>
                              </ListItemSecondaryAction>
                              <Box
                                sx={{
                                  ml: 2,
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <Radio
                                  checked={isSelected}
                                  value={item.id}
                                  name="selected-invoice"
                                  color="primary"
                                  inputProps={{ "aria-label": `Select invoice ${item.invoiceNumber}` }}
                                  onChange={() => {
                                    setSelectedInvoiceId(item.id);
                                    setDiscount(null);
                                    setPayError("");
                                  }}
                                />
                              </Box>
                            </ListItem>
                          </Paper>
                        );
                      })}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Grid container spacing={2.5}>
            <Grid size={{ xs: 12, md: 5 }}>
              <Card variant="outlined" sx={{ borderRadius: 3, height: "100%" }}>
                <CardContent sx={{ p: { xs: 2.25, md: 3 } }}>
                  <Stack spacing={2}>
                    <Typography variant="h6" fontWeight={800}>
                      Coupon code
                    </Typography>
                    {success && successContext === "coupon" && (
                      <Alert severity="success" onClose={() => setSuccess("")}>{success}</Alert>
                    )}
                    <TextField
                      fullWidth
                      size="small"
                      value={couponCode}
                      disabled={actionLoading || !hasPendingPayments}
                      onChange={(e) => {
                        setCouponCode(e.target.value.toUpperCase());
                        setDiscount(null);
                        setPayError("");
                      }}
                      placeholder="Enter coupon code"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocalOfferOutlinedIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Button
                      variant="outlined"
                      onClick={handleApplyCoupon}
                      disabled={
                        actionLoading ||
                        !hasPendingPayments ||
                        !couponCode.trim()
                      }
                      sx={{ textTransform: "none" }}
                    >
                      Apply coupon
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 7 }}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 3,
                  height: "100%",
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                <CardContent
                  sx={{
                    p: { xs: 2.25, md: 3 },
                    minWidth: 0,
                    overflow: "hidden",
                  }}
                >
                  <Stack spacing={2} sx={{ width: "100%", minWidth: 0 }}>
                    <Typography variant="h6" fontWeight={800}>
                      How to pay
                    </Typography>

                    <ToggleButtonGroup
                      exclusive
                      fullWidth
                      value={strategy}
                      onChange={(_, v) => {
                        if (v) {
                          setStrategy(v);
                          setPayError("");
                        }
                      }}
                      disabled={!hasPendingPayments || actionLoading}
                    >
                      {INVOICE_PAYMENT_STRATEGIES.map((item) => (
                        <ToggleButton
                          key={item.value}
                          value={item.value}
                          disabled={
                            item.value === "wallet" &&
                            (!hasSameCurrency || !isWalletEnough)
                          }
                          sx={{ textTransform: "none" }}
                        >
                          {item.label}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>

                    {strategy === "split" && hasSameCurrency ? (
                      <Box
                        sx={{
                          width: "100%",
                          minWidth: 0,
                          overflow: "hidden",
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ overflowWrap: "anywhere" }}
                        >
                          Amount from wallet (max{" "}
                          {formatMoney(maxWalletForInvoice, invoiceCurrency)})
                        </Typography>
                        <Box
                          sx={{
                            width: "100%",
                            minWidth: 0,
                            display: "flex",
                            justifyContent: "center",
                            px: 2,
                            py: 1,
                            boxSizing: "border-box",
                          }}
                        >
                          <Box
                            sx={{
                              width: "100%",
                              maxWidth: "380px",
                              display: "block",
                            }}
                          >
                            <Slider
                              value={walletPortion}
                              min={0}
                              max={maxWalletForInvoice}
                              step={0.01}
                              onChange={(_, v) => setWalletPayAmount(v)}
                              disabled={
                                actionLoading || maxWalletForInvoice <= 0
                              }
                              size="small"
                              sx={{
                                width: "100%",
                                "& .MuiSlider-thumb": {
                                  transition:
                                    "box-shadow 150ms cubic-bezier(0.4, 0, 0.2, 1)",
                                },
                              }}
                            />
                          </Box>
                        </Box>

                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Wallet amount"
                          value={walletPortion}
                          onChange={(e) =>
                            setWalletPayAmount(
                              clampNumber(
                                e.target.value,
                                0,
                                maxWalletForInvoice,
                              ),
                            )
                          }
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                {invoiceCurrency}
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            width: "100%",
                            minWidth: 0,
                            "& .MuiInputBase-root": {
                              width: "100%",
                            },
                          }}
                        />

                        <Alert
                          severity="info"
                          sx={{
                            width: "100%",
                            minWidth: 0,
                            boxSizing: "border-box",
                            overflowWrap: "anywhere",
                          }}
                        >
                          Remaining{" "}
                          {formatMoney(onlinePortion, invoiceCurrency)} will be
                          paid online.
                        </Alert>
                      </Box>
                    ) : null}

                    {(strategy === "online" || strategy === "split") &&
                    onlinePortion > 0 ? (
                      <>
                        <Typography variant="subtitle2" fontWeight={700}>
                          Online payment method
                        </Typography>
                        <FormControl fullWidth size="small">
                          <Select
                            value={onlineMethod}
                            onChange={(e) => setOnlineMethod(e.target.value)}
                            disabled={onlineGateways.length === 0}
                          >
                            {onlineGateways.map((item) => (
                              <MenuItem key={item.value} value={item.value}>
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  {getGatewayIcon(item.label)}
                                  <span>{item.label}</span>
                                </Stack>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </>
                    ) : null}

                    {strategy === "wallet" && isWalletEnough ? (
                      <Alert severity="success">
                        Full amount will be deducted from wallet.
                      </Alert>
                    ) : strategy === "split" ? (
                      <Alert severity="info" icon={<InfoOutlinedIcon />}>
                        Wallet portion is charged first; you are then redirected
                        for the rest.
                      </Alert>
                    ) : (
                      <Alert severity="info">
                        You will be redirected to the payment gateway.
                      </Alert>
                    )}

                    {payError ? (
                      <Alert severity="error" onClose={() => setPayError("")}>
                        {payError}
                      </Alert>
                    ) : null}

                    {success && successContext === "payment" && (
                      <Alert severity="success" onClose={() => setSuccess("")}>{success}</Alert>
                    )}

                    <Button
                      fullWidth
                      size="large"
                      variant="contained"
                      disabled={payDisabled}
                      onClick={handlePay}
                      endIcon={
                        actionLoading ? (
                          <CircularProgress color="inherit" size={18} />
                        ) : (
                          <ArrowForwardIcon />
                        )
                      }
                      sx={{
                        py: 1.35,
                        borderRadius: 2,
                        fontWeight: 800,
                        textTransform: "none",
                      }}
                    >
                      {actionLoading
                        ? "Processing..."
                        : strategy === "wallet"
                          ? "Pay from wallet"
                          : strategy === "split"
                            ? `Pay ${formatMoney(walletPortion, invoiceCurrency)} + online ${formatMoney(onlinePortion, invoiceCurrency)}`
                            : `Pay ${formatMoney(total, invoiceCurrency)} online`}
                    </Button>

                    <Button
                      fullWidth
                      variant="text"
                      startIcon={<ArrowBackIcon />}
                      onClick={() => navigate("/dashboard")}
                      sx={{ textTransform: "none" }}
                    >
                      Back to dashboard
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Stack>
      </Box>
    </Box>
  );
}

export default Wallet;
